/**
 * Shared status labels and badge styles — keep mobile, web, and docs in sync.
 * Source of truth: design-tokens/tokens.json (brand colors)
 */

export type LeaveStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

export const LEAVE_STATUS_LABELS: Record<LeaveStatus, string> = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};

/** Works with Tailwind on web (outline badges) and mobile (filled badges) */
export const LEAVE_STATUS_BADGE_CLASS: Record<LeaveStatus, string> = {
  PENDING: 'bg-amber-50 text-amber-800 border-amber-200',
  APPROVED: 'bg-green-50 text-green-800 border-green-200',
  REJECTED: 'bg-red-50 text-red-800 border-red-200',
  CANCELLED: 'bg-gray-50 text-gray-700 border-gray-200',
};

export const CLAIM_STATUS_BADGE_CLASS: Record<string, string> = {
  PENDING: LEAVE_STATUS_BADGE_CLASS.PENDING,
  MANAGER_APPROVED: 'bg-purple-50 text-purple-800 border-purple-200',
  APPROVED: LEAVE_STATUS_BADGE_CLASS.APPROVED,
  REJECTED: LEAVE_STATUS_BADGE_CLASS.REJECTED,
  PAID: 'bg-blue-50 text-blue-800 border-blue-200',
  CANCELLED: LEAVE_STATUS_BADGE_CLASS.CANCELLED,
  DRAFT: 'bg-gray-50 text-gray-700 border-gray-200',
  SUBMITTED: LEAVE_STATUS_BADGE_CLASS.PENDING,
  DOCS_REQUESTED: 'bg-sky-50 text-sky-800 border-sky-200',
};

export type ClaimStatus =
  | 'DRAFT'
  | 'PENDING'
  | 'MANAGER_APPROVED'
  | 'SUBMITTED'
  | 'APPROVED'
  | 'REJECTED'
  | 'PAID'
  | 'DOCS_REQUESTED'
  | 'CANCELLED';

export const CLAIM_STATUS_LABELS: Record<ClaimStatus, string> = {
  DRAFT: 'Draft',
  PENDING: 'Awaiting approval',
  MANAGER_APPROVED: 'Pending 2nd approval',
  SUBMITTED: 'Awaiting payment',
  APPROVED: 'Pending for Payment',
  REJECTED: 'Rejected',
  PAID: 'Paid',
  DOCS_REQUESTED: 'Action Required',
  CANCELLED: 'Cancelled',
};

export type AttendanceStatus = 'PRESENT' | 'ABSENT' | 'LEAVE' | 'HOLIDAY' | 'LATE' | 'HALF_DAY';

export const ATTENDANCE_STATUS_LABELS: Record<AttendanceStatus, string> = {
  PRESENT: 'Present',
  ABSENT: 'Absent',
  LEAVE: 'On leave',
  HOLIDAY: 'Holiday',
  LATE: 'Late',
  HALF_DAY: 'Half day',
};

/** Mobile Badge variant names (components/ui/badge). */
export type StatusBadgeVariant = 'warning' | 'success' | 'destructive' | 'info' | 'secondary';

export const LEAVE_STATUS_VARIANT: Record<LeaveStatus, StatusBadgeVariant> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'destructive',
  CANCELLED: 'secondary',
};

export const CLAIM_STATUS_VARIANT: Record<string, StatusBadgeVariant> = {
  DRAFT: 'secondary',
  PENDING: 'warning',
  MANAGER_APPROVED: 'warning',
  SUBMITTED: 'info',
  APPROVED: 'success',
  REJECTED: 'destructive',
  PAID: 'success',
  DOCS_REQUESTED: 'info',
  CANCELLED: 'secondary',
};

export const ATTENDANCE_STATUS_VARIANT: Record<string, StatusBadgeVariant> = {
  PRESENT: 'success',
  ABSENT: 'destructive',
  LEAVE: 'warning',
  HOLIDAY: 'secondary',
  LATE: 'warning',
  HALF_DAY: 'info',
};

export const PAYSLIP_STATUS_BADGE_CLASS: Record<string, string> = {
  PAID: 'border-green-300 text-green-700',
  PROCESSED: 'border-green-300 text-green-700',
  PENDING: 'border-yellow-300 text-yellow-700',
  DRAFT: 'border-gray-300 text-gray-700',
  FAILED: 'border-red-300 text-red-700',
};
